import React, { useState, useEffect } from 'react';
import {
  View,
  StatusBar,
  StyleSheet,
  Platform,
} from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-aware-scroll-view';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { StackNavigationProp } from '@react-navigation/stack';
import { RootStackParamList } from '@/navigation/RootNavigator';
import { useResponsive } from '@/hooks/common/useResponsive';
import { useToast } from '@/contexts/admin/ToastContext';
import { REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useThemeColors } from '@/hooks/residents/useThemeColors';
import { ReportReason } from '@/types/residents/marketplaceListing';
import {
  SellerChatModal,
  PhotoGalleryModal,
  ImageCarousel,
  ReportModal,
  InlineConversation,
} from '@/components/residents/marketplace';
import {
  SellerCard,
  ItemDetailsSection,
  ActionButtonRow,
} from '@/components/residents/marketplace/shared';
import {
  calculateCarouselDimensions,
} from '@/utils/marketplace';
import { useToggleSavedItemMutation, useCreateReportMutation, useLazyGetListingQuery, useGetConversationByListingQuery, profileApi } from '@/store/api';
import { useAppDispatch } from '@/store';
import { CACHE_TAGS } from '@/constants/residents/marketplaceConstants';
import { GradientBackground } from '@/components/residents/redesign';
import { BaseResidentMobileScreen } from '@/components/residents/mobile/BaseResidentMobileScreen';
import { BackLink } from '@/components/residents/mobile/BackLink';

type MarketplaceItemDetailNavigationProp = StackNavigationProp<RootStackParamList, 'MarketplaceItemDetail'>;
type MarketplaceItemDetailRouteProp = RouteProp<RootStackParamList, 'MarketplaceItemDetail'>;

const HP = REDESIGN_SPACING.horizontalPadding;
const MAX_CONTENT_WIDTH = 1200;

export const MarketplaceItemDetailScreen: React.FC = () => {
  const navigation = useNavigation<MarketplaceItemDetailNavigationProp>();
  const route = useRoute<MarketplaceItemDetailRouteProp>();
  const dispatch = useAppDispatch();
  const { layoutMode, width: screenWidth } = useResponsive();
  const insets = useSafeAreaInsets();
  const { showSuccess } = useToast();
  const { semantic } = useThemeColors();

  const isMobile = layoutMode === 'mobile';
  const isDesktop = layoutMode === 'desktop';

  const carouselDimensions = calculateCarouselDimensions(layoutMode, screenWidth);

  const { listing: initialListing } = route.params;
  const [listing, setListing] = useState(initialListing);
  const [isBookmarked, setIsBookmarked] = useState(initialListing.isBookmarked);
  const [isChatModalVisible, setIsChatModalVisible] = useState(false);
  const [isGalleryVisible, setIsGalleryVisible] = useState(false);
  const [galleryInitialIndex, setGalleryInitialIndex] = useState(0);
  const [isReportModalVisible, setIsReportModalVisible] = useState(false);
  const [hasAlreadyReported, setHasAlreadyReported] = useState(false);

  const [toggleSavedItem] = useToggleSavedItemMutation();
  const [createReport] = useCreateReportMutation();
  const [getListing] = useLazyGetListingQuery();

  const {
    data: existingConversation,
    refetch: refetchConversation,
  } = useGetConversationByListingQuery(initialListing.id);

  const hasExistingConversation = !!existingConversation;

  useEffect(() => {
    const trackViewAndRefresh = async () => {
      try {
        const freshListing = await getListing(initialListing.id).unwrap();
        if (freshListing) {
          setListing(freshListing);
          setIsBookmarked(freshListing.isBookmarked);
        }
        dispatch(profileApi.util.invalidateTags([{ type: CACHE_TAGS.RecentlyViewed, id: 'LIST' }]));
      } catch (error) {
        console.warn('Failed to fetch fresh listing data:', error);
      }
    };
    trackViewAndRefresh();
  }, [initialListing.id, getListing, dispatch]);

  const handleBookmarkPress = async () => {
    const newBookmarkState = !isBookmarked;
    setIsBookmarked(newBookmarkState);
    try {
      await toggleSavedItem(listing.id).unwrap();
      if (newBookmarkState) showSuccess('Added to your saved items!');
    } catch (error) {
      setIsBookmarked(!newBookmarkState);
      console.error('Failed to toggle saved item:', error);
    }
  };

  const handleContactSeller = () => setIsChatModalVisible(true);
  const handleCloseChatModal = () => { setIsChatModalVisible(false); refetchConversation(); };
  const handleReportListing = () => setIsReportModalVisible(true);
  const handleCloseReportModal = () => setIsReportModalVisible(false);

  const handleSubmitReport = async (reason: ReportReason, description?: string) => {
    try {
      await createReport({ listingId: listing.id, reason, description }).unwrap();
      setIsReportModalVisible(false);
      showSuccess('Report submitted. It will be reviewed by the moderation team.');
      setHasAlreadyReported(true);
    } catch (error: any) {
      if (error?.status === 409) setHasAlreadyReported(true);
      console.error('Failed to submit report:', error);
    }
  };

  const handleImagePress = (index: number) => { setGalleryInitialIndex(index); setIsGalleryVisible(true); };
  const handleCloseGallery = () => setIsGalleryVisible(false);
  const handleGalleryContactSeller = () => { setIsGalleryVisible(false); setTimeout(() => setIsChatModalVisible(true), 100); };
  const handleSellerPress = () => navigation.navigate('SellerProfile', { sellerId: listing.seller.id });

  const scrollContent = (
    <KeyboardAwareScrollView
      style={styles.scrollView}
      contentContainerStyle={[
        styles.content,
        isDesktop && styles.contentDesktop,
      ]}
      showsVerticalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      enableOnAndroid
      enableAutomaticScroll
      extraHeight={Platform.OS === 'ios' ? 140 : 100}
      keyboardOpeningTime={0}
      enableResetScrollToCoords={false}
    >
      <View style={[styles.contentWrapper, isDesktop && styles.contentWrapperDesktop]}>
        <BackLink label="Back to marketplace" onPress={() => navigation.goBack()} color={semantic.interactive.primary} />
        <View style={!isMobile && styles.carouselContainer}>
          <ImageCarousel
            images={listing.images.map(img => img.url)}
            onImagePress={handleImagePress}
            showBookmark
            isBookmarked={isBookmarked}
            onBookmarkPress={handleBookmarkPress}
            width={!isMobile ? carouselDimensions.width : undefined}
            height={!isMobile ? carouselDimensions.height : undefined}
            layoutMode={layoutMode}
            showArrows={!isMobile}
          />
        </View>

        <ItemDetailsSection
          price={listing.price}
          title={listing.title}
          description={listing.description}
          condition={listing.condition}
          pickupOption={listing.pickupOption}
        />

        <SellerCard seller={listing.seller} onPress={handleSellerPress} />

        {!hasExistingConversation && (
          <ActionButtonRow onContactPress={handleContactSeller} onReportPress={handleReportListing} />
        )}

        {hasExistingConversation && <InlineConversation listingId={listing.id} />}

        {hasExistingConversation && (
          <ActionButtonRow onReportPress={handleReportListing} hideContactButton />
        )}
      </View>
    </KeyboardAwareScrollView>
  );

  const modals = (
    <>
      <SellerChatModal visible={isChatModalVisible} onClose={handleCloseChatModal} listing={listing} />
      <PhotoGalleryModal
        visible={isGalleryVisible}
        onClose={handleCloseGallery}
        images={listing.images.map(img => img.url)}
        initialIndex={galleryInitialIndex}
        onContactSeller={handleGalleryContactSeller}
        onReport={handleReportListing}
      />
      <ReportModal
        visible={isReportModalVisible}
        onClose={handleCloseReportModal}
        onSubmit={handleSubmitReport}
        hasAlreadyReported={hasAlreadyReported}
      />
    </>
  );

  if (isDesktop) {
    return (
      <>
        <View style={styles.desktopContainer}>{scrollContent}</View>
        {modals}
      </>
    );
  }

  return (
    <>
      <GradientBackground>
        <StatusBar barStyle="light-content" />
        <BaseResidentMobileScreen screenName="Marketplace" darkBackground headerVariant="gradient" contentContainerStyle={{ paddingBottom: insets.bottom + 10 }}>
          {scrollContent}
        </BaseResidentMobileScreen>
      </GradientBackground>
      {modals}
    </>
  );
};

const styles = StyleSheet.create({
  desktopContainer: {
    flex: 1,
    backgroundColor: 'transparent',
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: HP,
    paddingBottom: 40,
    gap: 20,
  },
  contentDesktop: {
    alignItems: 'center',
    paddingBottom: 60,
  },
  contentWrapper: {
    gap: 20,
    width: '100%',
  },
  contentWrapperDesktop: {
    maxWidth: MAX_CONTENT_WIDTH,
  },
  carouselContainer: {
    alignItems: 'center',
  },
});
